import * as Sentry from "@sentry/react";

// Sentry is opt-in only — disabled by default (no ingest.us.sentry.io calls).
const dsnFromEnv =
  import.meta.env.VITE_GLUCERA_SENTRY_DSN ?? import.meta.env.VITE_SENTRY_DSN;
const sentryEnabled =
  import.meta.env.VITE_GLUCERA_ENABLE_SENTRY === "true" &&
  Boolean(String(dsnFromEnv ?? "").trim());

type LogLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";

export interface GluceraContext {
  userId?: string;
  tenantId?: string;
  feature?: string;
  action?: string;
  [key: string]: unknown;
}

interface GluceraConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  sessionSampleRate: number;
  enableUserFeedback: boolean;
  enableSessionReplay: boolean;
}

// Environment configuration
enum Environment {
  DEVELOPMENT = "development",
  STAGING = "staging",
  PRODUCTION = "production",
}

const currentEnv =
  (import.meta.env.MODE as Environment) ?? Environment.DEVELOPMENT;
const isDevelopment = currentEnv === Environment.DEVELOPMENT;
const isProduction = currentEnv === Environment.PRODUCTION;
// Note: isStaging available for future use if needed

// Sensitive data patterns to filter out
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /key/i,
  /auth/i,
  /credential/i,
  /ssn/i,
  /credit.?card/i,
  /phone/i,
  /email/i,
];

// PII data sanitization function
const sanitizePII = (data: any): any => {
  if (!data) {
    return data;
  }

  if (typeof data === "string") {
    // Check if string contains sensitive patterns
    for (const pattern of SENSITIVE_PATTERNS) {
      if (pattern.test(data)) {
        return "[REDACTED]";
      }
    }
    return data;
  }

  if (typeof data === "object" && data !== null) {
    const sanitized: any = Array.isArray(data) ? [] : {};

    for (const [key, value] of Object.entries(data)) {
      // Check if key name suggests sensitive data
      const isSensitiveKey = SENSITIVE_PATTERNS.some(pattern =>
        pattern.test(key)
      );

      if (isSensitiveKey) {
        sanitized[key] = "[REDACTED]";
      } else {
        sanitized[key] = sanitizePII(value);
      }
    }
    return sanitized;
  }

  return data;
};

// Configuration based on environment
const getGluceraConfig = (): GluceraConfig => {
  const dsn = String(dsnFromEnv ?? "").trim();

  const baseConfig = {
    dsn,
    environment: import.meta.env.VITE_GLUCERA_SENTRY_ENVIRONMENT ?? currentEnv,
    enableUserFeedback:
      import.meta.env.VITE_GLUCERA_ENABLE_USER_FEEDBACK === "true",
    enableSessionReplay:
      import.meta.env.VITE_GLUCERA_ENABLE_SESSION_REPLAY === "true",
  };

  switch (currentEnv) {
    case Environment.PRODUCTION:
      return {
        ...baseConfig,
        tracesSampleRate: parseFloat(
          import.meta.env.VITE_GLUCERA_PERFORMANCE_SAMPLE_RATE ?? "0.1"
        ),
        sessionSampleRate: parseFloat(
          import.meta.env.VITE_GLUCERA_SESSION_SAMPLE_RATE ?? "0.05"
        ),
      };
    case Environment.STAGING:
      return {
        ...baseConfig,
        tracesSampleRate: parseFloat(
          import.meta.env.VITE_GLUCERA_PERFORMANCE_SAMPLE_RATE ?? "0.5"
        ),
        sessionSampleRate: parseFloat(
          import.meta.env.VITE_GLUCERA_SESSION_SAMPLE_RATE ?? "0.2"
        ),
      };
    default: // Development
      return {
        ...baseConfig,
        tracesSampleRate: parseFloat(
          import.meta.env.VITE_GLUCERA_PERFORMANCE_SAMPLE_RATE ?? "1.0"
        ),
        sessionSampleRate: parseFloat(
          import.meta.env.VITE_GLUCERA_SESSION_SAMPLE_RATE ?? "1.0"
        ),
        enableSessionReplay: false, // Disable in dev to avoid noise unless explicitly enabled
      };
  }
};

const config = getGluceraConfig();

if (sentryEnabled) {
  Sentry.init({
  dsn: config.dsn,
  environment: config.environment,

  // Performance monitoring
  tracesSampleRate: config.tracesSampleRate,

  // Session replay (for user behavior analysis)
  replaysSessionSampleRate: config.sessionSampleRate,
  replaysOnErrorSampleRate: 1.0, // Always capture replay on error

  // Enhanced integrations
  integrations: [
    // React integration with enhanced error boundaries
    Sentry.browserTracingIntegration(),

    // Session replay for production debugging
    ...(config.enableSessionReplay
      ? [
          Sentry.replayIntegration({
            maskAllText: true, // Mask all text for privacy
            blockAllMedia: true, // Block all media for privacy
          }),
        ]
      : []),

    // User feedback integration
    ...(config.enableUserFeedback
      ? [
          Sentry.feedbackIntegration({
            colorScheme: "system" as const,
            showBranding: false,
          }),
        ]
      : []),
  ],

  // Enhanced beforeSend with better filtering and sanitization
  beforeSend(event, _hint) {
    // Development mode handling
    if (isDevelopment) {
      return null; // Don't send to Sentry in dev
    }

    // Filter out known non-critical errors
    const ignoredErrors = [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "ChunkLoadError",
      "Loading chunk",
      "Script error",
    ];

    if (event.exception?.values?.[0]?.value) {
      const errorMessage = event.exception.values[0].value;
      if (ignoredErrors.some(ignored => errorMessage.includes(ignored))) {
        return null;
      }
    }

    // Sanitize PII data
    if (event.extra) {
      event.extra = sanitizePII(event.extra);
    }

    if (event.contexts) {
      event.contexts = sanitizePII(event.contexts);
    }

    if (event.user) {
      // Keep only non-sensitive user data
      event.user = {
        id: event.user.id,
        username: event.user.username ? "[REDACTED]" : undefined,
        email: event.user.email ? "[REDACTED]" : undefined,
      };
    }

    // Add Bite Balance-specific tags
    event.tags = {
      ...event.tags,
      project: "glucera",
      component: "react-frontend",
    };

    return event;
  },

  // IP anonymization for privacy
  sendDefaultPii: false,

  // Enhanced context capture
  beforeBreadcrumb(breadcrumb) {
    // Filter out noisy breadcrumbs in production
    if (isProduction) {
      if (breadcrumb.category === "console" && breadcrumb.level === "log") {
        return null;
      }

      if (
        breadcrumb.category === "navigation" &&
        breadcrumb.message?.includes("?")
      ) {
        // Remove query parameters from navigation breadcrumbs
        breadcrumb.message = breadcrumb.message.split("?")[0];
      }
    }

    return breadcrumb;
  },
  });
}

// Enhanced console management for development and production
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
const originalConsoleLog = console.log;

// Suppressed warning patterns
const SUPPRESSED_WARNINGS = [
  "Warning: findDOMNode is deprecated",
  "findDOMNode",
  "React DevTools",
  "Warning: validateDOMNesting",
  "Warning: Each child in a list should have a unique",
  "Warning: Failed prop type",
  "Warning: componentWillReceiveProps has been renamed",
  "Warning: componentWillMount has been renamed",
  "Warning: componentWillUnmount has been renamed",
  "Download the React DevTools",
  "%cDownload the React DevTools",
];

// Production: Suppress ALL console logs, warnings
if (isProduction) {
  console.log = () => {};
  console.warn = () => {};
  console.debug = () => {};
  // Only allow critical errors in production
  console.error = (...args) => {
    if (typeof args[0] === "string") {
      const message = args[0];
      // Suppress known React warnings even in error logs
      if (SUPPRESSED_WARNINGS.some(warning => message.includes(warning))) {
        return;
      }
    }
    originalConsoleError.apply(console, args);
  };
} else if (isDevelopment) {
  // Development: Suppress only specific warnings
  console.error = (...args) => {
    if (typeof args[0] === "string") {
      const message = args[0];
      if (SUPPRESSED_WARNINGS.some(warning => message.includes(warning))) {
        if (import.meta.env.VITE_GLUCERA_DEBUG_SUPPRESSED_WARNINGS === "true") {
          console.debug("[Glucera - Suppressed Warning]:", ...args);
        }
        return;
      }
    }
    originalConsoleError.apply(console, args);
  };

  console.warn = (...args) => {
    if (typeof args[0] === "string") {
      const message = args[0];
      if (SUPPRESSED_WARNINGS.some(warning => message.includes(warning))) {
        if (import.meta.env.VITE_GLUCERA_DEBUG_SUPPRESSED_WARNINGS === "true") {
          console.debug("[Glucera - Suppressed Warning]:", ...args);
        }
        return;
      }
    }
    originalConsoleWarn.apply(console, args);
  };
}

// Structured logging utility for Bite Balance
export const logger = {
  debug: (message: string, context?: Record<string, unknown>) => {
    if (isDevelopment || import.meta.env.VITE_GLUCERA_ENABLE_DEBUG === "true") {
      console.debug(`[Glucera Debug] ${message}`, context);
    }
  },

  info: (message: string, context?: Record<string, unknown>) => {
    if (!isProduction) {
      console.info(`[Glucera] ${message}`, context);
    }
  },

  warn: (message: string, context?: Record<string, unknown>) => {
    if (!isProduction) {
      console.warn(`[Glucera Warning] ${message}`, context);
    }
  },

  error: (
    message: string,
    error?: Error,
    context?: Record<string, unknown>
  ) => {
    // Always log errors, but send to Sentry in production
    if (!isProduction) {
      console.error(`[Glucera Error] ${message}`, error, context);
    }

    if (isProduction && sentryEnabled) {
      captureException(error ?? new Error(message), context);
    }
  },
};

// Enhanced utility functions for manual error tracking in Bite Balance
export const captureException = (
  error: Error | null | undefined,
  context?: GluceraContext
) => {
  if (!error || !sentryEnabled) {
    return;
  }

  // Set Bite Balance-specific context
  if (context) {
    Sentry.setContext("glucera", sanitizePII(context));
  }

  // Add additional context for better debugging
  Sentry.setTag("glucera.error_type", error.constructor.name);

  if (isDevelopment) {
    logger.error("Exception captured:", error, context);
    return;
  }

  Sentry.captureException(error);
};

export const captureMessage = (
  message: string,
  level: LogLevel = "info",
  context?: GluceraContext
) => {
  if (!sentryEnabled) {
    if (isDevelopment) {
      logger.info(`Message captured (${level}): ${message}`, context);
    }
    return;
  }
  if (context) {
    Sentry.setContext("glucera", sanitizePII(context));
  }

  if (isDevelopment) {
    logger.info(`Message captured (${level}): ${message}`, context);
    return;
  }

  Sentry.captureMessage(`[Glucera] ${message}`, level);
};

// Enhanced performance monitoring helper for Bite Balance
export const startTransaction = (
  name: string,
  op: string = "glucera",
  context?: GluceraContext
) => {
  if (!sentryEnabled) {
    if (isDevelopment) {
      logger.debug(`Transaction started: ${name}`, { op, context });
    }
    return undefined;
  }
  if (context) {
    Sentry.setContext("glucera", sanitizePII(context));
  }

  // Set tags for better categorization
  if (context?.feature) {
    Sentry.setTag("glucera.feature", context.feature);
  }
  if (context?.action) {
    Sentry.setTag("glucera.action", context.action);
  }

  return Sentry.startSpan(
    {
      name: `[Glucera] ${name}`,
      op,
    },
    span => {
      if (isDevelopment) {
        logger.debug(`Transaction started: ${name}`, { op, context });
      }
      return span;
    }
  );
};

// User identification helper
export const identifyUser = (
  userId: string,
  userContext?: {
    tenantId?: string;
    role?: string;
    features?: string[];
  }
) => {
  if (!sentryEnabled) {
    if (isDevelopment) {
      logger.debug("User identified", { userId, context: sanitizePII(userContext) });
    }
    return;
  }

  const sanitizedContext = sanitizePII(userContext);

  Sentry.setUser({
    id: userId,
    ...sanitizedContext,
  });

  Sentry.setContext("glucera_user", {
    tenant_id: userContext?.tenantId,
    role: userContext?.role,
    feature_count: userContext?.features?.length,
  });

  if (isDevelopment) {
    logger.debug("User identified", { userId, context: sanitizedContext });
  }
};

// Feature usage tracking
export const trackFeatureUsage = (
  feature: string,
  action: string,
  metadata?: Record<string, unknown>
) => {
  if (!sentryEnabled) return;
  const context: GluceraContext = {
    feature,
    action,
    ...metadata,
  };

  captureMessage(`Feature used: ${feature}.${action}`, "info", context);
};

// Performance measurement utility
export const measurePerformance = async <T>(
  name: string,
  operation: () => Promise<T> | T,
  context?: GluceraContext
): Promise<T> => {
  const startTime = performance.now();

  try {
    const result = await operation();
    const duration = performance.now() - startTime;

    if (isDevelopment) {
      logger.debug(
        `Performance: ${name} completed in ${duration.toFixed(2)}ms`,
        context
      );
    }

    // Track performance metrics using custom event
    if (sentryEnabled) {
      captureMessage(
        `Performance: ${name} completed in ${duration.toFixed(2)}ms`,
        "info",
        {
          ...context,
          operation: name,
          duration,
          performance_metric: true,
        }
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    captureException(error as Error, {
      ...context,
      operation: name,
      duration,
    });

    throw error;
  }
};

// API error tracking helper
export const trackAPIError = (
  endpoint: string,
  method: string,
  statusCode: number,
  error?: Error,
  context?: Record<string, unknown>
) => {
  if (!sentryEnabled) return;

  const apiContext: GluceraContext = {
    feature: "api",
    action: "request_failed",
    endpoint: endpoint.split("?")[0], // Remove query params
    method,
    statusCode,
    ...context,
  };

  if (error) {
    captureException(error, apiContext);
  } else {
    captureMessage(
      `API Error: ${method} ${endpoint} (${statusCode})`,
      "error",
      apiContext
    );
  }
};
